import { PositionRangeAutocomplete } from '@tom-ui/ui'
import { FC } from 'react'

interface Props {
  label: string
  axisNames: string[]
  minValue: number | null | undefined
  maxValue: number | null | undefined
  onChange: (min: number | null, max: number | null) => void
}

/** Converts 1-based min/max indices to a range display string, e.g. "A-D". */
function minMaxToRangeString(
  labels: string[],
  min?: number | null,
  max?: number | null,
): string | undefined {
  if (min == null) return undefined
  if (max == null || max === min) return labels[min - 1]
  return `${labels[min - 1]}-${labels[max - 1]}`
}

/** Converts a range string back to 1-based min/max indices. */
function rangeStringToMinMax(
  labels: string[],
  value?: string,
): { min: number | null; max: number | null } {
  if (!value) return { min: null, max: null }
  const parts = value.split('-')
  const min = labels.findIndex(l => l === parts[0]) + 1
  const max = labels.findIndex(l => l === (parts[1] ?? parts[0])) + 1
  return { min: min || null, max: max || null }
}

/**
 * Range field for a named position axis (bays, rows, or any string-labelled axis).
 * Accepts and emits 1-based integer min/max values, displaying them via PositionRangeAutocomplete.
 * Null min/max means no restriction (full extent).
 */
export const BlockAxisRangeField: FC<Props> = ({
  label,
  axisNames,
  minValue,
  maxValue,
  onChange,
}) => {
  const rangeString = minMaxToRangeString(axisNames, minValue, maxValue)

  return (
    <PositionRangeAutocomplete
      label={label}
      options={axisNames}
      value={rangeString?.split('-') ?? []}
      handleChange={value => {
        const { min, max } = rangeStringToMinMax(axisNames, value)
        onChange(min, max)
      }}
    />
  )
}
